import { EndReason, MatchPhase, TICK_RATE, Team } from "@arena/shared";
import { TEAM_COLORS, TEAM_NAMES, fighterCss } from "../palette.ts";

export interface ScoreRow {
  id: string;
  name: string;
  characterId: string;
  color: number;
  score: number;
  /** Meter filled, 0–1. */
  slam: number;
  slamReady: boolean;
  connected: boolean;
  team: number;
  hp: number;
  maxHp: number;
  respawnTicks: number;
  metamorphTicks?:number;
  /** Arena KO: eliminated in the match under way (D42). */
  out?: boolean;
}

export interface ResultRow {
  id: string;
  name: string;
  color: number;
  score: number;
  left: boolean;
  /** Display name of the champion played. */
  champion: string;
  team: number;
  /** Arena KO: still standing at the end. */
  standing?: boolean;
}

export interface MatchView {
  training?: boolean;
  koMode: boolean;
  phase: string;
  timeLeft: number;
  localId: string;
  /** Local meter, 0–1, and whether ★ works now (predicted). */
  localSlam: number;
  localReady: boolean;
  players: ScoreRow[];
  results: ResultRow[];
  winner: string;
  endReason: string;
  /** Team match: the two team scores, the goal and the winning team (0 = draw). */
  teams: boolean;
  teamScores: [number, number];
  goal: number;
  winnerTeam: number;
  /** Arena KO: the minute of deathmatch after time ran out. */
  overtime?: boolean;
}

const GO_BANNER_MS = 900;
const URGENT_SECONDS = 10;

/**
 * Match HUD (GDD §7.2: Slam meter, timer and score first): timer and score
 * chips at the top, the local meter filling the ★ button, countdown
 * banner, and the results card. All values come from the
 * replicated state; the results card shows the same ranking on every client.
 */
export class MatchHud {
  private readonly timer = element("match-timer");
  private readonly scores = element("match-scores");
  private readonly teamBar = element("match-teams");
  private readonly banner = element("banner");
  private readonly slamButton = document.querySelector<HTMLElement>('[data-button="Slam"]');
  private readonly results = element("results");
  private readonly resultsTitle = element("results-title");
  private readonly resultsReason = element("results-reason");
  private readonly resultsList = element("results-list");
  private readonly resultsNext = element("results-next");
  private lastPhase = "";
  private lastOvertime = false;
  private goUntil = 0;
  private flashUntil = 0;
  private flashText = "";
  private rendered = { timer: "", scores: "", banner: "", results: "", next: "", slam: "", teams: "" };

  /** Hides everything, e.g. after leaving the room. */
  clear(): void {
    this.timer.hidden = true;
    this.banner.hidden = true;
    this.results.hidden = true;
    this.teamBar.hidden = true;
    this.scores.replaceChildren();
    this.lastPhase = "";
    this.rendered = { timer: "", scores: "", banner: "", results: "", next: "", slam: "", teams: "" };
  }

  /** A short centre-screen message, e.g. when a Super Slam scores. */
  flash(text: string, ms = 1000): void {
    this.flashText = text;
    this.flashUntil = performance.now() + ms;
  }

  update(view: MatchView): void {
    const now = performance.now();
    if (view.phase === MatchPhase.Playing && this.lastPhase === MatchPhase.Countdown) this.goUntil = now + GO_BANNER_MS;
    this.lastPhase = view.phase;
    if (view.overtime && !this.lastOvertime) this.flash("DEATHMATCH! · 1 MINUTO", 2200);
    this.lastOvertime = view.overtime ?? false;

    this.renderTimer(view);
    this.renderTeams(view);
    this.renderScores(view);
    this.renderSlamButton(view);
    this.renderBanner(view, now);
    this.renderResults(view);
  }

  private renderTimer(view: MatchView): void {
    const shown = view.phase === MatchPhase.Playing || view.phase === MatchPhase.Ended;
    const seconds = view.phase === MatchPhase.Playing ? view.timeLeft : 0;
    const label = view.overtime && view.phase === MatchPhase.Playing ? "DEATHMATCH" : view.koMode ? "KO" : "Slam";
    const text = shown ? `${label} · ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}` : "";
    if (text === this.rendered.timer) return;
    this.rendered.timer = text;
    this.timer.hidden = !shown;
    this.timer.textContent = text;
    this.timer.classList.toggle("urgent", view.phase === MatchPhase.Playing && (seconds <= URGENT_SECONDS || label === "DEATHMATCH"));
  }

  /** Team match: "Blu 7 · 15 · 4 Rossi" under the timer, the goal in the middle. */
  private renderTeams(view: MatchView): void {
    const shown = view.teams && (view.phase === MatchPhase.Playing || view.phase === MatchPhase.Ended);
    // Arena KO: how many each team still has standing; no goal to reach.
    const standing = (team: number) => view.players.filter((p) => p.team === team && !p.out).length;
    const values: [number, number] = view.koMode ? [standing(Team.Blue), standing(Team.Red)] : view.teamScores;
    const key = shown ? JSON.stringify([values, view.goal, view.koMode]) : "";
    if (key === this.rendered.teams) return;
    this.rendered.teams = key;
    this.teamBar.hidden = !shown;
    if (!shown) return;
    const side = (team: number, score: number) => {
      const el = document.createElement("span");
      el.className = "team-score";
      el.style.setProperty("--color", TEAM_COLORS[team] ?? "");
      const name = document.createElement("span");
      name.textContent = TEAM_NAMES[team] ?? "";
      const value = document.createElement("b");
      value.textContent = String(score);
      el.append(name, value);
      return el;
    };
    const goal = document.createElement("small");
    goal.textContent = view.koMode ? "in piedi" : `a ${view.goal}`;
    goal.title = view.koMode ? "Combattenti ancora in piedi" : "Punti per vincere";
    this.teamBar.replaceChildren(side(Team.Blue, values[0]), goal, side(Team.Red, values[1]));
  }

  private renderScores(view: MatchView): void {
    // In KO the floating bars carry health information; keep the upper arena clear.
    const koCombat = view.koMode && (view.phase === MatchPhase.Playing || (view.training && view.phase === MatchPhase.Lobby));
    this.scores.hidden = koCombat || (view.phase !== MatchPhase.Playing && view.phase !== MatchPhase.Ended && !(view.training && view.koMode));
    if (this.scores.hidden) return;
    // Local player first, then by score: the chips do not jump around in a duel. In a team match, by team.
    const rows = view.players.filter(row=>!view.training || row.id===view.localId).sort(
      (a, b) =>
        (view.teams ? a.team - b.team : 0) ||
        Number(b.id === view.localId) - Number(a.id === view.localId) ||
        b.score - a.score,
    );
    const key = JSON.stringify([view.koMode,rows.map((r) => [r.id, r.name, r.characterId, r.maxHp, r.score, Math.round(r.slam * 20), r.slamReady, r.connected, r.team, r.hp, Math.ceil(r.respawnTicks/TICK_RATE), r.out])]);
    if (key === this.rendered.scores) return;
    this.rendered.scores = key;
    this.scores.replaceChildren(
      ...rows.map((row) => {
        const chip = document.createElement("div");
        chip.className = "score-chip";
        chip.classList.toggle("self", row.id === view.localId);
        chip.classList.toggle("ready", row.slamReady);
        chip.classList.toggle("offline", !row.connected);
        chip.style.setProperty("--color", fighterCss(row.color, row.team));
        const name = document.createElement("span");
        name.className = "name";
        name.textContent = row.name;
        name.title = row.name;
        const score = document.createElement("b");
        score.textContent = `${row.score}${view.koMode ? " KO" : ""}`;
        const meter = document.createElement("span");
        meter.className = "mini";
        meter.style.setProperty("--fill", row.slam.toFixed(2));
        meter.setAttribute("aria-label", `Barra Slam ${Math.round(row.slam * 100)}%`);
        const photo = document.createElement("img");
        photo.src = `/proto2d/portraits/closeups/${row.characterId}.png`;
        photo.alt = row.characterId;
        photo.className = "hud-portrait";
        chip.append(photo, name, score, meter);
        if (view.koMode) {
          const hp = document.createElement("span");
          hp.className = "health-readout";
          hp.textContent = row.out ? "Eliminato" : row.respawnTicks > 0 ? `Rientro ${Math.ceil(row.respawnTicks/TICK_RATE)}s` : `${row.hp} HP`;
          const health = document.createElement("div");
          health.className = "hud-health";
          health.classList.toggle("low", row.hp <= row.maxHp * 0.3);
          health.setAttribute("role", "progressbar");
          health.setAttribute("aria-label", `Vita di ${row.name}`);
          health.setAttribute("aria-valuemin", "0");
          health.setAttribute("aria-valuemax", String(row.maxHp));
          health.setAttribute("aria-valuenow", String(row.hp));
          const fill = document.createElement("i");
          fill.style.width = `${100 * row.hp / Math.max(1, row.maxHp)}%`;
          health.append(fill, hp);
          chip.append(health);
        }
        return chip;
      }),
    );
  }

  private renderSlamButton(view: MatchView): void {
    if (!this.slamButton) return;
    const key = `${view.localSlam.toFixed(2)}|${view.localReady}`;
    if (key === this.rendered.slam) return;
    this.rendered.slam = key;
    this.slamButton.style.setProperty("--slam", view.localSlam.toFixed(2));
    this.slamButton.classList.toggle("ready", view.localReady);
    this.slamButton.setAttribute("aria-label", view.localReady ? "Super Slam pronta" : `Super Slam, barra al ${Math.round(view.localSlam * 100)}%`);
  }

  private renderBanner(view: MatchView, now: number): void {
    let text = "";
    let kind = "";
    const local = view.players.find(p=>p.id === view.localId);
    if (view.phase === MatchPhase.Intro || view.phase === MatchPhase.MapIntro) {
      text = "";
    } else if (view.koMode && view.phase === MatchPhase.Playing && local && local.respawnTicks > 0) {
      text = `KO · Rientro tra ${Math.ceil(local.respawnTicks/TICK_RATE)} s`;
      kind = "respawn";
    } else if (now < this.flashUntil) {
      text = this.flashText;
      kind = "flash";
    } else if (view.phase === MatchPhase.Countdown) {
      text = String(Math.max(1, view.timeLeft));
      kind = "count";
    } else if (now < this.goUntil) {
      text = "VIA!";
      kind = "count";
    } else if((local?.metamorphTicks??0)>0){
      text=`${local!.characterId==='auramennone'?'AURA':local!.characterId==='antonietta'?'PARRUCCA':local!.characterId==='giovanna'?'A CAVALLO':'METAMORFOSI'} · ${Math.ceil(local!.metamorphTicks!/TICK_RATE)} s`;kind='info';
    }
    const key = `${kind}|${text}`;
    if (key === this.rendered.banner) return;
    this.rendered.banner = key;
    this.banner.hidden = text === "";
    this.banner.textContent = text;
    this.banner.className = kind;
  }

  private renderResults(view: MatchView): void {
    const shown = view.phase === MatchPhase.Ended && view.results.length > 0;
    const key = shown
      ? JSON.stringify([view.results, view.winner, view.winnerTeam, view.teamScores, view.endReason, view.localId, view.koMode])
      : "";
    if (key !== this.rendered.results) {
      this.rendered.results = key;
      this.results.hidden = !shown;
      if (shown) {
        const winner = view.results.find((r) => r.id === view.winner);
        if (view.teams) {
          this.resultsTitle.textContent = view.winnerTeam ? `Vincono i ${TEAM_NAMES[view.winnerTeam]}!` : "Pareggio!";
          const score = `${TEAM_NAMES[Team.Blue]} ${view.teamScores[0]} – ${view.teamScores[1]} ${TEAM_NAMES[Team.Red]}`;
          const reason = view.koMode && view.endReason === EndReason.LastStanding ? "Ultima squadra in piedi"
            : (view.koMode ? KO_REASONS[view.endReason] : undefined) ?? REASONS[view.endReason] ?? "";
          this.resultsReason.textContent = `${score} · ${reason}`;
        } else {
          const survivors = view.results.filter((r) => r.standing).length;
          this.resultsTitle.textContent = winner ? `Vince ${winner.name}!` : view.koMode && survivors > 1 ? `Pareggio tra ${survivors} sopravvissuti!` : "Pareggio!";
          this.resultsReason.textContent = (view.koMode ? KO_REASONS[view.endReason] : undefined) ?? REASONS[view.endReason] ?? "";
        }
        // Team match MVP: the best individual score (the winning team first on a tie, like the rows).
        const best = Math.max(0, ...view.results.map((r) => r.score));
        const mvp = view.teams && best > 0 ? view.results.find((r) => r.score === best)?.id : undefined;
        this.resultsList.replaceChildren(
          ...view.results.map((row) => {
            const li = document.createElement("li");
            li.classList.toggle("self", row.id === view.localId);
            // A draw after the deathmatch: everyone still standing shares it.
            const sharesDraw = view.koMode && !view.teams && !view.winner && row.standing === true;
            li.classList.toggle("winner", row.id === view.winner || (view.teams && row.team === view.winnerTeam) || sharesDraw);
            li.style.setProperty("--color", fighterCss(row.color, row.team));
            const name = document.createElement("span");
            name.className = "name";
            name.textContent = row.name + (row.id === view.localId ? " (tu)" : "") + (row.left ? " · uscito" : "");
            const champion = document.createElement("small");
            champion.className = "champion-name";
            champion.textContent = row.champion;
            name.append(" ", champion);
            if (row.id === mvp) {
              const tag = document.createElement("span");
              tag.className = "mvp";
              tag.textContent = "MVP";
              name.append(" ", tag);
            }
            if (view.koMode) {
              const fate = document.createElement("span");
              fate.className = row.standing ? "fate standing" : "fate";
              fate.textContent = row.standing ? "in piedi" : "eliminato";
              name.append(" ", fate);
            }
            const score = document.createElement("b");
            score.textContent = `${row.score} ${view.koMode ? "KO" : row.score === 1 ? "punto" : "punti"}`;
            li.append(name, score);
            return li;
          }),
        );
      }
    }
    if (!shown) return;
    const next = `Ritorno alla lobby tra ${view.timeLeft} s`;
    if (next !== this.rendered.next) {
      this.rendered.next = next;
      this.resultsNext.textContent = next;
    }
  }
}

const REASONS: Record<string, string> = {
  [EndReason.Score]: "Punteggio obiettivo raggiunto",
  [EndReason.Time]: "Tempo scaduto",
  [EndReason.Abandon]: "Gli avversari hanno lasciato la partita",
};

/** Arena KO, to the last blood (D42). */
const KO_REASONS: Record<string, string> = {
  [EndReason.LastStanding]: "Ultimo rimasto in piedi",
  [EndReason.Time]: "Deathmatch finito: chi è ancora in piedi pareggia",
};

function element(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} mancante in index.html`);
  return el;
}
